'use strict';

import * as Sentry from "@sentry/browser";
import IAssessmentPackageManagerClient from '../../serviceworkerBridgeClients/IAssessmentPackageManagerClient';
import AssessmentPackageMetadata from '../../../../../serviceworker/src/models/AssessmentPackageMetadata';
import PWABootstrap from '../../pwa/PWABootstrap';
import { clearSessionResumeStorage } from '../../utils/sessionResumeStorage';

export class PWAService {

    //static TEST_ARCHIVE_FILE_NAME = '/testdata/IB-eAssessment-103009-Familiarization 2017 (English)-EN.ibassessment';
    //static TEST_ARCHIVE_FILE_NAME = '/testdata/IB-eAssessment-116114-BIOLOGY-2024-EN.ibassessment';
    static TEST_ARCHIVE_FILE_NAME = '/testdata/scienMOEENGTZ0XXXX.ibassessment';
    //static TEST_ARCHIVE_FILE_NAME = '/testdata/IB-eAssessment-117558-INTERDISC-2019-FR.ibassessment';



    static LAST_LOADED_ARCHIVE_KEY = 'lastLoadedArchiveKey';
    static CURRENT_METADATA_KEY = 'currentAssessmentMetadata';

    constructor($http, $q, AssessmentPackageManagerClient) {
        console.log('PWAService: Initializing');
        this.$http = $http;
        this.$q = $q;
        this.downloadProgress = 0;
        this.maxDownloadProgress = 0;
        this.assessmentPackageManager = AssessmentPackageManagerClient;
        this.currentMetadata = null;
        this._provisionedSchoolCode = null;
        this._provisionedSchoolCodeReason = '';
    }

    get lastLoadedArchiveKey() {
        const key = localStorage.getItem(PWAService.LAST_LOADED_ARCHIVE_KEY) || null;
        console.log(`PWAService: Getting lastLoadedArchiveKey: ${key}`);
        return key;
    }

    set lastLoadedArchiveKey(value) {
        console.log(`PWAService: Setting lastLoadedArchiveKey: ${value}`);
        if (value) {
            localStorage.setItem(PWAService.LAST_LOADED_ARCHIVE_KEY, value);
        } else {
            localStorage.removeItem(PWAService.LAST_LOADED_ARCHIVE_KEY);
        }
    }

    async sendSentrySessionIdToServiceWorker() {
        let sessionId;
        try {
            const currentHub = Sentry.getCurrentHub();
            const scope = currentHub && currentHub.getScope();
            const session = scope && scope.getSession();
            sessionId = session && session.sid;
        } catch (error) {
            console.warn('PWAService: Error getting Sentry session ID:', error);
            sessionId = null;
        }
        if (sessionId && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'sentrySessionId', sessionId: sessionId });
        } else {
            console.warn('PWAService: Unable to send Sentry session ID to service worker');
        }
    }
    convertToAssessmentMetadata(metadata, file) {

        return {
            filename: file.name,
            filesize: file.size,
            school_code: metadata.school_code || null,
            timestamp: metadata.timestamp || null,
            package_type: metadata.package_type,
            valid_from: metadata.valid_from,
            valid_to: metadata.valid_to,
            env: metadata.env,
            assessment_uuid: metadata.assessment_uuid,
            subject: metadata.subject,
            year: metadata.year,
            month: metadata.month,
            title: metadata.title,
            language: metadata.language,
            input_language: metadata.input_language,
            system_lang_selectable: metadata.system_lang_selectable,
            response_upload_api_url: metadata.response_upload_api_url,
            hybrid_response_api_url: metadata.hybrid_response_api_url
        };
    }


    async downloadAndSendToServiceWorker(url = PWAService.TEST_ARCHIVE_FILE_NAME, metadata) {
        console.log('PWAService: Starting downloadAndSendToServiceWorker');
        await PWABootstrap.ready();

        const deferred = this.$q.defer();

        console.log(`PWAService: Starting download of ${url}`);

        // derive filename from url using proper url parsing
        const urlObj = new URL(url);
        const filename = urlObj.pathname.split('/').pop();

        this.$http.get(url, {
            responseType: 'blob',
            eventHandlers: {
                progress: (event) => {
                    //console.log("PWAService: Raw progress event:", event);
                    if (event.lengthComputable) {
                        const newProgress = Math.round((event.loaded / event.total) * 100);
                        this.downloadProgress = Math.max(newProgress, this.maxDownloadProgress);
                        this.maxDownloadProgress = this.downloadProgress;
                        //console.log(`PWAService: Download progress updated: ${this.downloadProgress}%`);
                    } else {
                        console.log("PWAService: Progress event is not length computable");
                    }
                }
            }
        }).then(async response => {
            console.log("PWAService: Download completed, processing response");
            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const file = new File([blob], filename, { type: 'application/octet-stream' });

            console.log(`PWAService: Feeding .ibassessment file to AssessmentPackageManager: ${file.name}`);

            const storageInfo = await this.checkStorageQuota();
            if (storageInfo !== null) {
                const requiredBytes = file.size * 2;
                if (storageInfo.available < requiredBytes) {
                    const availableMB = Math.round(storageInfo.available / 1024 / 1024);
                    const requiredMB = Math.round(requiredBytes / 1024 / 1024);
                    throw new Error(
                        `STORAGE_INSUFFICIENT:${availableMB}:${requiredMB}`
                    );
                }
            }

            try {
                await this.assessmentPackageManager.feedDownloadedFile(file, this.convertToAssessmentMetadata(metadata, file));
                this.lastLoadedArchiveKey = url;
                console.log('PWAService: Successfully fed file to AssessmentPackageManager');
                deferred.resolve();
            } catch (error) {
                console.error("PWAService: Error feeding file to AssessmentPackageManager:", error);
                deferred.reject(error);
            }
        }).catch(error => {
            console.error("PWAService: Error downloading file:", error);
            deferred.reject(error);
        });

        return deferred.promise;
    }

    getDownloadProgress() {
        return this.downloadProgress;
    }

    resetDownloadProgress() {
        console.log('PWAService: Resetting download progress');
        this.downloadProgress = 0;
        this.maxDownloadProgress = 0;
    }

    handleIbAssessmentFile(file) {
        console.log(`PWAService: Handling .ibassessment file: ${file.name}`);
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const arrayBuffer = e.target.result;
                    console.log(`PWAService: File read successfully, size: ${arrayBuffer.byteLength} bytes`);
                    await this.sendToServiceWorker(arrayBuffer, file.name);
                    resolve();
                } catch (error) {
                    console.error('PWAService: Error in handleIbAssessmentFile:', error);
                    reject(error);
                }
            };
            reader.onerror = (error) => {
                console.error('PWAService: Error reading file:', error);
                reject(error);
            };
            reader.readAsArrayBuffer(file);
        });
    }

    async sendToServiceWorker(arrayBuffer, fileName) {
        console.log(`PWAService: Sending file to service worker: ${fileName}`);
        await PWABootstrap.ready();

        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            const file = new File([arrayBuffer], fileName, { type: 'application/octet-stream' });

            this.lastLoadedArchiveKey = fileName;
            console.log(`PWAService: Updated lastLoadedArchiveKey to ${fileName}`);

            navigator.serviceWorker.controller.postMessage({
                type: 'openAssessmentArchive',
                archiveFile: file,
                cacheKey: fileName
            });
            console.log('PWAService: Sent openAssessmentArchive message to service worker');

            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    navigator.serviceWorker.removeEventListener('message', messageListener);
                    console.error('PWAService: openAssessmentArchive timed out after 120s');
                    reject(new Error('Opening assessment archive timed out'));
                }, 120000);

                const messageListener = (event) => {
                    console.log('PWAService: Received message from service worker:', event.data);
                    if (event.data && event.data.type === 'assessmentOpened') {
                        clearTimeout(timeout);
                        console.log("PWAService: Assessment file opened successfully");
                        navigator.serviceWorker.removeEventListener('message', messageListener);
                        resolve();
                    } else if (event.data && event.data.type === 'error') {
                        clearTimeout(timeout);
                        console.error("PWAService: Error opening assessment:", event.data.error);
                        navigator.serviceWorker.removeEventListener('message', messageListener);
                        reject(new Error(event.data.error));
                    }
                };

                navigator.serviceWorker.addEventListener('message', messageListener);
            });
        } else {
            console.error("PWAService: Service worker is not controlling the page yet.");
            throw new Error("Service worker is not controlling the page yet.");
        }
    }

    async resetServiceWorker() {
        console.log('PWAService: Resetting service worker');
        await PWABootstrap.ready();

        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            this.lastLoadedArchiveKey = null;
            console.log('PWAService: Cleared lastLoadedArchiveKey');

            // Clear session resume data
            clearSessionResumeStorage();
            console.log('PWAService: Cleared session resume data');

            navigator.serviceWorker.controller.postMessage({
                type: 'resetServiceWorker'
            });
            console.log('PWAService: Sent resetServiceWorker message to service worker');

            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    navigator.serviceWorker.removeEventListener('message', messageListener);
                    console.warn('PWAService: resetServiceWorker timed out after 10s');
                    reject(new Error('Service worker reset timed out'));
                }, 10000);

                const messageListener = (event) => {
                    console.log('PWAService: Received message from service worker:', event.data);
                    if (event.data && event.data.type === 'serviceWorkerReset') {
                        clearTimeout(timeout);
                        console.log("PWAService: Service worker has been reset");
                        navigator.serviceWorker.removeEventListener('message', messageListener);
                        resolve();
                    }
                };
                navigator.serviceWorker.addEventListener('message', messageListener);
            });
        } else {
            console.error("PWAService: Service worker is not controlling the page yet.");
            return Promise.reject(new Error("Service worker not ready"));
        }
    }

    /**
     * Saves current assessment metadata to localStorage and memory
     * @param {Object} metadata - The metadata to save
     */
    saveCurrentMetadata(metadata) {
        if (!metadata) return;

        console.log('PWAService: Saving current assessment metadata', metadata);
        this.currentMetadata = metadata;

        try {
            localStorage.setItem(PWAService.CURRENT_METADATA_KEY, JSON.stringify(metadata));
        } catch (e) {
            console.warn('PWAService: Failed to save metadata to localStorage', e);
        }
    }

    /**
     * Gets current assessment metadata from memory or localStorage
     * @returns {Object|null} The current assessment metadata or null if not available
     */
    getCurrentMetadata() {
        if (this.currentMetadata) {
            return this.currentMetadata;
        }

        try {
            const storedMetadata = localStorage.getItem(PWAService.CURRENT_METADATA_KEY);
            if (storedMetadata) {
                this.currentMetadata = JSON.parse(storedMetadata);
                return this.currentMetadata;
            }
        } catch (e) {
            console.warn('PWAService: Failed to retrieve metadata from localStorage', e);
        }

        return null;
    }

    /**
     * Gets the response upload API URL from the current assessment metadata
     * @returns {string|null} The API URL or null if not available
     */
    getResponseUploadApiUrl() {
        const metadata = this.getCurrentMetadata();
        if (metadata && metadata.response_upload_api_url) {
            return metadata.response_upload_api_url;
        }

        // Fallback to hybrid_response_api_url if available
        if (metadata && metadata.hybrid_response_api_url) {
            return metadata.hybrid_response_api_url;
        }

        return null;
    }

    /**
     * Telemetry ingest URL for the currently loaded package, or '' when unknown.
     *
     * Derived from the package's response-upload host, so it is available as soon as
     * a package is loaded, which is what lets telemetry start before login. A device
     * with no package at all (a virgin kiosk on its very first QR scan) has no
     * endpoint to report to and legitimately returns ''.
     */
    getTelemetryUrl() {
        try {
            const uploadUrl = this.getResponseUploadApiUrl() || '';
            if (!uploadUrl) return '';
            return new URL(uploadUrl).origin + '/pwa-api/telemetry/';
        } catch (e) {
            console.warn('PWAService: Could not derive telemetry URL:', e);
            return '';
        }
    }

    /**
     * Resolves the school code to stamp on telemetry, with its provenance.
     *
     * School packages carry their school code in the package metadata. Specimen
     * packages never do, because they are not school-scoped, so the CMS emits
     * `school_code: null` for them. Nor does the "Load Test Specimen" dev path,
     * which reports package_type 'school' but ships no code at all. That is why the
     * gate is "metadata has no school code" rather than package_type: it covers
     * both, and it can never change the value a school package already reports.
     *
     * @returns {Promise<{schoolCode: number|null, source: string}>}
     */
    resolveTelemetrySchoolCode(metadata) {
        const meta = metadata || {};
        if (meta.school_code != null) {
            return this.$q.when({ schoolCode: meta.school_code, source: 'package' });
        }

        return this.$q.when(this.getProvisionedSchoolCode())
            .then((rawCode) => {
                if (rawCode == null) {
                    // The reason matters: an unprovisioned school Chromebook and a
                    // developer laptop both land here, and reporting both as 'none'
                    // made the former unactionable.
                    return {
                        schoolCode: null,
                        source: this.getProvisionedSchoolCodeReason() || 'none'
                    };
                }
                // Managed config delivers whatever the admin entered, in practice a
                // zero-padded string ('048016'); package metadata delivers an int
                // (48016). parseInt normalises both into one groupable column,
                // matching ib-main where school_code is a PositiveIntegerField.
                const parsed = parseInt(rawCode, 10);
                if (Number.isNaN(parsed)) {
                    return { schoolCode: null, source: 'cfg_non_numeric' };
                }
                return { schoolCode: parsed, source: 'managed_config' };
            })
            .catch((error) => {
                console.warn('PWAService: Could not resolve provisioned school code:', error);
                return { schoolCode: null, source: 'resolve_threw' };
            });
    }

    async getProvisionedSchoolCode() {
        // Memoize successful lookups only. Callers on the critical path (telemetry
        // session start, right after login) must not pay the retry ladder below a
        // second time, but a null result may simply mean the policy has not been
        // pushed yet, so later callers have to stay free to retry.
        if (this._provisionedSchoolCode) {
            return this._provisionedSchoolCode;
        }

        // Dev-mode override: set via browser console with
        // localStorage.setItem('devSchoolCode', '123456')
        // @ts-ignore - __BUILD_NO__ is defined by webpack DefinePlugin
        const isDevMode = typeof __BUILD_NO__ !== 'undefined' && __BUILD_NO__ === 'dev-local';
        const devOverride = isDevMode && localStorage.getItem('devSchoolCode');
        if (devOverride) {
            const code = String(devOverride).trim();
            if (/^\d+$/.test(code)) {
                console.log('PWAService: Using dev school code override:', code);
                this._provisionedSchoolCode = code;
                return code;
            }
            console.warn('PWAService: devSchoolCode is not numeric, ignoring');
        }

        // Production path: Chrome OS managed config.
        if (typeof navigator.managed?.getManagedConfiguration !== 'function') {
            this._provisionedSchoolCodeReason = 'unmanaged';
            return null;
        }

        // Cold-boot race: managed config delivery is policy-pushed and can take
        // 200–2000ms after session start (esp. after a kiosk auto-relaunch under
        // EphemeralMode). Retry with bounded backoff so we don't false-fire
        // NO_SCHOOL_CODE on a properly-provisioned device that just hasn't
        // received its policy yet. The PWA start page keeps the user on the
        // pre-access-check state (accessCheckPending=true) during this window.
        const delays = [0, 300, 800, 1500];
        let lastReason = 'no_attempts';
        for (let attempt = 0; attempt < delays.length; attempt++) {
            if (delays[attempt] > 0) {
                await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
            }
            try {
                const cfg = await navigator.managed.getManagedConfiguration(['primarySchoolCode']);
                const rawCode = cfg && cfg.primarySchoolCode;
                if (rawCode == null) {
                    lastReason = 'cfg_empty';
                    continue;
                }
                const code = String(rawCode).trim();
                if (code.length === 0) {
                    // Whitespace-only is operationally indistinguishable from a
                    // missing key — treat it as 'not delivered yet', not a format bug.
                    lastReason = 'cfg_empty';
                    continue;
                }
                if (!/^\d+$/.test(code)) {
                    lastReason = 'cfg_non_numeric';
                    continue;
                }
                if (attempt > 0) {
                    console.log(`PWAService: Provisioned school code found (attempt ${attempt + 1}/${delays.length})`);
                } else {
                    console.log('PWAService: Provisioned school code found');
                }
                this._provisionedSchoolCode = code;
                return code;
            } catch (err) {
                console.warn(`PWAService: getManagedConfiguration attempt ${attempt + 1}/${delays.length} threw:`, err.name, err.message);
                lastReason = 'cfg_threw';
            }
        }
        // Single exhaustion warning — keeps kiosk logs clean and surfaces the
        // dominant failure mode. The "not numeric" detail is reserved for the
        // misformat case so admins can act on it.
        if (lastReason === 'cfg_non_numeric') {
            console.warn('PWAService: getProvisionedSchoolCode exhausted retries, provisioned value is not numeric');
        } else {
            console.warn(`PWAService: getProvisionedSchoolCode exhausted retries (last reason: ${lastReason})`);
        }
        // Retain the diagnosis. Telemetry reports it as schoolCodeSource, which
        // otherwise labelled an unprovisioned school Chromebook and a developer
        // laptop identically as 'none' and made the former unactionable.
        this._provisionedSchoolCodeReason = lastReason;
        return null;
    }

    /**
     * Why the last getProvisionedSchoolCode() call returned null.
     *
     * One of 'unmanaged' (no enterprise management, e.g. local dev), 'cfg_empty'
     * (managed but the policy has not been delivered), 'cfg_non_numeric',
     * 'cfg_threw', or 'no_attempts'. Empty string when a code was found.
     */
    getProvisionedSchoolCodeReason() {
        return this._provisionedSchoolCode ? '' : (this._provisionedSchoolCodeReason || '');
    }

    async isManaged() {
        let isMaybeManaged = typeof navigator.managed?.getManagedConfiguration === 'function';

        if (!isMaybeManaged) {
            console.error('PWAService: Not running under enterprise management');
            return false;
        }

        try {
            const cfg = await navigator.managed.getManagedConfiguration(['primarySchoolCode']);
            console.log('PWAService: Managed config:', cfg);
            return true;
        } catch (err) {
            console.warn('PWAService: Error getting managed config:', err.name, err.message);
            return false;
        }
    }

    isFullScreen() {
        console.log('PWAService: Checking if running in full screen mode');

        const isDisplayModeFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
        console.log('PWAService: Display mode fullscreen:', isDisplayModeFullscreen);

        const isChromeOS = this.isChromeOS();
        console.log('PWAService: Running on ChromeOS:', isChromeOS);

        const isFullScreen = isDisplayModeFullscreen && isChromeOS;
        console.log('PWAService: Is full screen mode:', isFullScreen);

        return isFullScreen;
    }

    isChromeOS() {
        return navigator.userAgent.includes('CrOS');
    }

    /**
     * Checks whether a raw school code string is a valid, non-dummy code.
     * Strips leading zeros before comparing against the blocklist.
     */
    isValidSchoolCode(rawCode) {
        const BLOCKED_SCHOOL_CODES = [1, 1234, 9999, 12345, 99999, 999999];
        const code = parseInt(rawCode, 10);
        if (Number.isNaN(code) || code <= 0) return false;
        return !BLOCKED_SCHOOL_CODES.includes(code);
    }

    /**
     * Determines whether the current device is allowed to access the app.
     * Returns { allowed, reason } where reason is an AccessBlockedReason
     * string constant (or null when allowed).
     *
     * Check order: ChromeOS → Kiosk mode → School code presence → School code validity
     */
    async getDeviceAccessStatus() {
        // 1. Must be on ChromeOS
        if (!this.isChromeOS()) {
            return { allowed: false, reason: 'NOT_CHROMEOS' };
        }

        // 2. Must be in kiosk mode (managed + fullscreen)
        const managed = await this.isManaged();
        const fullscreen = this.isFullScreen();
        if (!managed || !fullscreen) {
            return { allowed: false, reason: 'NOT_KIOSK' };
        }

        // 3. Must have a school code provisioned
        const rawSchoolCode = await this.getProvisionedSchoolCode();
        if (rawSchoolCode === null) {
            return { allowed: false, reason: 'NO_SCHOOL_CODE' };
        }

        // 4. School code must not be a dummy/test code
        if (!this.isValidSchoolCode(rawSchoolCode)) {
            return { allowed: false, reason: 'INVALID_SCHOOL_CODE' };
        }

        return { allowed: true, reason: null };
    }

    async checkStorageQuota() {
        if (!navigator.storage || !navigator.storage.estimate) {
            return null;
        }
        try {
            const estimate = await navigator.storage.estimate();
            return {
                usage: estimate.usage || 0,
                quota: estimate.quota || 0,
                available: (estimate.quota || 0) - (estimate.usage || 0)
            };
        } catch (error) {
            console.warn('PWAService: Error checking storage quota:', error);
            return null;
        }
    }

    async activateWakeLock() {
        console.log('PWAService: Activating wake lock');
        // Create a reference for the Wake Lock.
        let wakeLock = null;

        // create an async function to request a wake lock
        try {
            wakeLock = await navigator.wakeLock.request("screen");
            console.log("PWAService: Wake Lock is active!");
        } catch (err) {
            console.error('PWAService: Error getting wake lock:', err.name, err.message);
        }
    }

    /**
     * Check service worker status (loader initialized and authentication state)
     * @returns {Promise<{instanceId: string, loaderInitialized: boolean, isAuthenticated: boolean, userUUID: string|null}>}
     */
    async checkServiceWorkerStatus() {
        console.log('PWAService: Checking service worker status');
        await PWABootstrap.ready();

        if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
            console.error('PWAService: Service worker not available (no controller)');
            return Promise.reject(new Error('Service worker not available'));
        }

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                navigator.serviceWorker.removeEventListener('message', messageListener);
                console.warn('PWAService: Service worker status check timed out after 5s');
                reject(new Error('Service worker status check timed out'));
            }, 5000);

            const messageListener = (event) => {
                if (event.data && event.data.type === 'serviceWorkerStatus') {
                    clearTimeout(timeout);
                    navigator.serviceWorker.removeEventListener('message', messageListener);
                    console.log('PWAService: Received service worker status', event.data);
                    resolve({
                        instanceId: event.data.instanceId,
                        loaderInitialized: event.data.loaderInitialized,
                        isAuthenticated: event.data.isAuthenticated,
                        userUUID: event.data.userUUID
                    });
                }
            };

            navigator.serviceWorker.addEventListener('message', messageListener);
            navigator.serviceWorker.controller.postMessage({ type: 'checkServiceWorkerStatus' });
        });
    }

    /**
     * Check if session resume is possible
     * @returns {boolean}
     */
    hasSessionResumeData() {
        const key = sessionStorage.getItem('sessionResumeKey');
        const assessmentId = localStorage.getItem('sessionResumeAssessmentId');
        const timestamp = sessionStorage.getItem('sessionResumeTimestamp');

        if (!key || !assessmentId) {
            return false;
        }

        // Check if session is fresh (within 4 hours)
        if (timestamp) {
            const sessionAge = Date.now() - parseInt(timestamp, 10);
            const fourHoursInMs = 4 * 60 * 60 * 1000;
            if (sessionAge > fourHoursInMs) {
                // Stale session, clear it
                console.log('PWAService: Session resume data expired, clearing');
                clearSessionResumeStorage();
                return false;
            }
        }

        return true;
    }

    /**
     * Get session resume data
     * @returns {{key: string|null, assessmentId: string|null, route: string|null}}
     */
    getSessionResumeData() {
        return {
            key: sessionStorage.getItem('sessionResumeKey'),
            assessmentId: localStorage.getItem('sessionResumeAssessmentId'),
            route: sessionStorage.getItem('sessionResumeRoute')
        };
    }

}

export default function registerPWAService(services) {
    console.log('PWAService: Registering PWAService');
    services.service('PWAService', ['$http', '$q', 'AssessmentPackageManagerClient', PWAService]);
}
